/**
 * Plain-language explanations shown in tooltips, help icons and field hints across the app.
 * Written for someone who opens the site for the first time: what an element is, why it is there,
 * what happens when it is used and what to do next. Many texts are read by more than one role
 * (an employee, a project manager, an administrator), so they avoid "ask your manager" style advice
 * that is wrong for the manager reading it. Kept in one place so the wording can be reviewed and
 * corrected without hunting through components.
 */
import { CONFIG } from './config';
import { HOURS_PER_DAY } from './publicHolidays';
import { ROUTES } from './routes';
import { SUBMIT_STATUSES } from './submitStatuses';

/** One line per menu item, shown when the pointer rests on it. */
export const NAV_DESCRIPTIONS: Record<string, string> = {
  [ROUTES.TIMESHEET]: 'Log your hours per day, project and activity, and submit each month for approval.',
  [ROUTES.MY_SUBMITS]: 'Send your monthly hours to project managers for approval and follow the status of each project.',
  [ROUTES.TIME_OFF]: 'Your approved leaves, public holidays and working days of your office.',
  [ROUTES.ANALYTICS]: 'A chart of how your hours split between projects, activities or clients over a period.',
  [ROUTES.MY_PROJECTS]: 'Projects you can log time to. Hide the ones you no longer need from the project list in the Timesheet.',
  [ROUTES.REPORTS.SUMMARY]: 'Hour totals grouped by project, activity, client or date: your own hours, and for managers also their projects.',
  [ROUTES.REPORTS.TIME_AUDIT]: 'Finds hours worth a second look (too many in a day, weekend work) and people who logged nothing.',
  [ROUTES.APPROVALS]: 'Approve, reject or return to draft the monthly hours submitted for your projects (administrators: all projects).',
  [ROUTES.DASHBOARD_PM]: 'Month overview of your projects (administrators: all): submission statuses, team hours and alerts.',
  [ROUTES.SETTINGS.PROJECTS]: 'Create projects and manage the ones you lead: which activities can be logged and who can log time (administrators: all projects).',
  [ROUTES.SETTINGS.ACTIVITIES]: 'Types of work (development, meeting, vacation...) that people choose when they log time.',
  [ROUTES.SETTINGS.RESOURCES]: 'People who can sign in and log time: account, department, office, line manager and weekly hours.',
  [ROUTES.SETTINGS.PERMISSIONS]: 'Choose which projects and activities a specific person is allowed to log time to.',
  [ROUTES.SETTINGS.WORKING_HOURS]: 'Working days per month for each office. They set how many hours people must log each month.',
  [ROUTES.SETTINGS.DELEGATIONS]: 'Let one person open and fill in another person\'s timesheet.',
};

/**
 * What each monthly submission status means for one project. Read both by the employee who submitted
 * and by the project manager who reviews, so the wording fits either reader.
 */
export const SUBMIT_STATUS_DESCRIPTIONS: Record<string, string> = {
  [SUBMIT_STATUSES.DRAFT]: 'Draft - not sent for approval yet. The hours can still be changed.',
  [SUBMIT_STATUSES.SUBMITTED]: 'Submitted - sent to the project manager and waiting for approval. '
    + 'The hours are locked unless the manager sends them back.',
  [SUBMIT_STATUSES.APPROVED]: 'Approved - accepted by the project manager. The hours are locked; '
    + 'the project manager or an administrator can return them to draft if something must change.',
  [SUBMIT_STATUSES.REJECTED]: 'Rejected - sent back by the project manager with a comment. '
    + 'The hours can be corrected and submitted again.',
};

export const HELP_TEXTS = {
  COMMON: {
    FILL_REQUIRED: 'Fill in the required fields marked with *',
    REQUIRED_NOT_SAVED: 'Required. Changes are not saved until this field is filled in.',
    NO_HOURS: 'no hours',
  },

  LAYOUT: {
    COLLAPSE_SIDEBAR: 'Collapse the menu to icons to get more room for the page',
    EXPAND_SIDEBAR: 'Expand the menu to show page names',
    ENVIRONMENT_TAG: (tag: string): string =>
      `Test site (${tag}). Hours entered here are kept separately and do not reach the real timesheet.`,
    PROFILE: 'Your profile: role, department, office, weekly working hours and display settings',
    FEEDBACK: 'Report a problem, ask a question or suggest an improvement. Opens in a new tab.',
    THEME_TO_LIGHT: 'Switch to the light theme',
    THEME_TO_DARK: 'Switch to the dark theme',
  },

  TIMESHEET: {
    TAB_WEEK: 'Enter hours day by day for one week',
    TAB_MONTH: 'See the whole month at a glance. Click a day to open its week. Submit All at the bottom sends the month '
      + 'for approval.',
    PREV_WEEK: 'Previous week',
    NEXT_WEEK: 'Next week',
    PICK_DATE: 'Pick a date in the calendar to jump to its week',
    TODAY: 'Jump to the current week',
    MONTH_SWITCHER: 'This week falls in two months, and each month is submitted separately. Pick the month to fill in; '
      + 'the other month\'s days are striped and locked until you switch. The totals and Submit All at the bottom follow '
      + 'the month you pick.',
    RESOURCE_SELECTOR: 'Choose whose timesheet to work on. "Myself" is your own; the other names are people you may fill in for '
      + '(set on the Delegations page). You can enter their hours, but they submit the month themselves.',
    DELEGATION_BANNER: 'You are filling in the timesheet of',
    DELEGATION_BANNER_NOTE: 'Hours you enter are saved to their timesheet, not yours; they submit the month themselves. '
      + 'Click X to return to your own.',
    HOW_TO: 'How to log time:\n'
      + '1. Click "Add project", pick the project, then the kind of work (activity).\n'
      + '2. Click a day and type the hours in quarter hours (1.5 = 1 h 30 min). Tab or Enter moves to the next day, '
      + 'arrow Up / Down adds or removes 15 minutes, Esc cancels.\n'
      + '3. Hours save by themselves about 2 seconds after you leave a cell - wait for "Changes saved" before you switch '
      + 'the week or the page.\n'
      + '4. At the end of the month, click Submit All to send the month for approval.',
    EMPTY_WEEK: 'No hours logged for this week yet. Click "Add project" to start.',
    EMPTY_WEEK_LOCKED: 'No hours logged for this week. This month is already submitted, so no rows can be added.',
    COLLAPSE_ALL: 'Collapse all projects to their header rows',
    EXPAND_ALL: 'Expand all projects',
    LEGEND: (dailyHours: number): string =>
      `Each weekday is colored by its total compared with a full working day (${dailyHours}h): `
      + `yellow - less, green - exactly ${dailyHours}h, red - more. Weekend columns stay gray.`,
    DESCRIPTION_COLUMN: 'Optional short note about the work on this row, for example a task or ticket number. It is saved automatically.',
    TOTAL_COLUMN: 'Hours on this row for the whole week',
    ADD_PROJECT: 'Add a project to this week. After picking the project, pick the activity you worked on.',
    ADD_ACTIVITY: 'Add another activity row to this project for this week',
    PROJECT_SEARCH: 'Type a project name; projects you used recently are listed first. Projects you hid on My Projects '
      + 'are not listed - if a project is still missing, ask its project manager.',
    ACTIVITY_SEARCH: 'Pick the kind of work you did - the row is added at once. If the activity you need is missing, '
      + 'ask the project manager.',
    CANCEL_ADD: 'Cancel adding this row (Esc)',
    PROJECT_REQUIRED: 'Required. After the project, pick an activity.',
    PROJECT_MISSING: 'Pick a project, or press Esc to cancel.',
    // Shown under the activity field of the add row: keep both short enough to fit on one line there
    ACTIVITY_REQUIRED: 'Required. Picking one saves the row.',
    ACTIVITY_MISSING: 'Not saved yet - pick an activity.',
    PENDING_HOURS_CELL: 'Pick an activity first, then enter hours here.',
    CHANGE_ACTIVITY: 'Click to change the activity of this row',
    DELETE_ROW: (hours: number): string =>
      `Delete this row${hours > 0 ? ` and its ${hours}h` : ''} at once - this cannot be undone.`,
    LOCKED_CELL: 'Locked: this project\'s hours for the month are submitted or approved. '
      + 'To change them, the project manager has to send them back.',
    OUT_OF_CONTEXT_CELL: 'This day belongs to the other month. Switch the month at the top of the page to log hours for it.',
    REJECTED_COMMENT: (comment: string): string => `Project manager's comment: ${comment}`,
    TOTAL_ROW: 'Total hours per day for all projects, and the total for the week',
    WEEK_PROGRESS: 'Hours logged this week against a full five-day week. Public holidays are not subtracted, '
      + 'and in a week split between two months only the selected month counts.',
    MONTH_PROGRESS: 'Hours logged this month out of the hours required for it (working days of your office calendar '
      + 'times your daily hours). In brackets: the weekdays left in the month and the hours they add up to.',
    MONTH_STATUS: 'Overall status of the month across all your projects. It shows Rejected or Submitted if at least one '
      + 'project is in that state, and Approved only when every project is approved.',
    SUBMIT_ALL: (month: string): string =>
      `Send your ${month} hours on all projects to the project managers for approval. `
      + 'Possible once the month\'s required hours are logged; after that the hours are locked.',
    SUBMIT_ALL_BLOCKED: (reported: number, required: number): string =>
      `You have logged ${reported}h of the ${required}h required this month. Log the missing `
      + `${Math.max(0, +(required - reported).toFixed(2))}h, including vacation or sick days, to enable Submit All.`,
    SUBMIT_ALL_CONFIRM: (month: string): string =>
      `Submit all your projects for ${month}? The hours will be locked; to change them later, `
      + 'the project manager has to send them back.',
  },

  AUTO_FILL: {
    TITLE: 'Suggested rows for this empty week',
    HELP: 'This week has no rows yet. The rows below are the project and activity pairs you logged time to in at least '
      + `${CONFIG.SUGGESTION_MIN_WEEKS} of your last ${CONFIG.SUGGESTION_WEEKS_ANALYZED} weeks.\n`
      + 'Applying them adds the rows to this week with 0 hours (and the last description you used), '
      + 'so you only need to type the hours. Nothing is added until you click Apply.',
    AVERAGE: 'Your average hours per week on this row in recent weeks. For reference only - hours are not filled in.',
    APPLY_ALL: 'Add all suggested rows to this week with 0 hours',
    CUSTOMIZE: 'Choose which suggested rows to add',
    APPLY_SELECTED: 'Add only the checked rows to this week',
    DISMISS: 'Hide the suggestions until you open the Timesheet again',
  },

  LEAVE_SYNC: {
    HELP: 'Your approved leave requests from the Leave Management site (where you request leave) that are not in your '
      + 'timesheet yet.\n'
      + 'Apply adds your daily hours (for example 8h) of time off for each missing weekday, in whatever week the leave falls. '
      + 'These hours count toward the hours required for the month.',
    MISSING_DAYS: 'Weekdays of this leave that have no time-off hours in your timesheet yet',
    APPLY_ALL: 'Add all leaves listed here to your timesheet',
    CUSTOMIZE: 'Choose which leaves to add',
    APPLY_SELECTED: 'Add only the checked leaves to your timesheet',
    DISMISS: 'Hide this panel. It appears again the next time you open the Timesheet.',
  },

  CALENDAR: {
    PREV_MONTH: 'Previous month',
    NEXT_MONTH: 'Next month',
    TODAY: 'Jump to the current month',
    PROJECT_FILTER: 'Show only the hours of the projects you pick (day colors then count only those projects). '
      + 'Leave empty to see all.',
    DAY: (dayLabel: string, hoursLabel: string): string => `${dayLabel}: ${hoursLabel}. Click to open this week.`,
    LEGEND: (dailyHours: number): string =>
      `Each day is colored by the hours logged that day compared with a full working day (${dailyHours}h): `
      + `yellow - less, green - exactly ${dailyHours}h, red - more. Weekdays with no hours have no color, `
      + 'weekends without hours are gray, and the framed day is today.',
    MONTH_TOTAL: 'Hours logged this month on all projects out of the hours required for the month, and the percentage filled',
  },

  TIME_OFF: {
    PAGE: 'Your approved leaves from the Leave Management site (where you request leave) and the public holidays '
      + 'of the chosen office, for the selected year. Leaves that ended more than 30 days ago are not shown.\n'
      + 'Colors: leave types as in the table, blue - public holiday or transferred day off, '
      + 'green frame - a weekend day that is a working day, gray - weekend. Hover a day for details.',
    LOCATION: 'Public holidays and transferred working days differ by country - choose which office\'s calendar to show.',
    PREV_YEAR: 'Previous year',
    NEXT_YEAR: 'Next year',
    SUMMARY_TABLE: 'Number of leave days per type and month. Weekends are not counted.',
    MONTH_STATS: 'Working days and working hours in this month for the chosen office',
    YEAR_TOTAL: 'Working days and working hours in the year for the chosen office '
      + '(weekends, public holidays and transferred days off are excluded)',
  },

  MY_PROJECTS: {
    PAGE: 'Projects you can log time to. Click a project to hide it from the list you pick from in the Timesheet '
      + '(for example a finished project), or to show it again; hours already logged stay. '
      + 'People are added to a project by its project manager or an administrator.',
    SHOWN: 'Listed when you add a project in the Timesheet. Click to hide it.',
    HIDDEN: 'Not listed when you add a project in the Timesheet. Click to list it again.',
    HIDDEN_LABEL: 'Hidden from the Timesheet project list',
  },

  SUBMITS: {
    PAGE: 'Your hours per project for the chosen month and where each one stands. Draft rows are not sent yet - '
      + 'click Submit All to send them; each project manager then approves or rejects. Expand a row to see its history.',
    COMMENT_COLUMN: 'Comment from the project manager, for example the reason for a rejection',
    PREV_MONTH: 'Previous month',
    NEXT_MONTH: 'Next month',
    YEAR: 'Choose the year',
    CURRENT_MONTH: 'Jump to the current month',
    HISTORY: 'Show or hide the history of this submission',
  },

  APPROVALS: {
    PAGE: 'Submissions for the month chosen above, on your projects (administrators: all projects). '
      + 'Only those waiting for review are listed; check "Show completed" to also see approved and rejected ones '
      + 'and return them to draft. Approve accepts the hours; Reject (a comment is required) sends them back '
      + 'for correction. Select several rows to act on all of them at once.',
    HOURS_COLUMN: 'Hours on this project, and in "of", the person\'s required hours for the whole month on all projects',
    SHOW_COMPLETED: 'Also show submissions that are already approved or rejected, for example to return them to draft',
    RESOURCE_FILTER: 'Show only submissions of these people',
    PROJECT_FILTER: 'Show only submissions for these projects',
    APPROVE: 'Accept these hours. They stay locked.',
    REJECT: 'Send the hours back to the employee with a comment. They can correct them and submit again.',
    REVERT: 'Return these hours to draft so the employee can change them and submit again',
    REMINDER: 'Email this person a general reminder to submit their time, with an optional note',
    APPROVE_SELECTED: 'Approve all selected submissions that are waiting for review',
    REJECT_SELECTED: 'Reject all selected submissions that are waiting for review, with one comment for all',
    REVERT_SELECTED: 'Return all selected approved or rejected submissions to draft',
    SELECT_ALL: 'Select or clear all rows',
    HISTORY: 'Show or hide the history and comment of this submission',
    REJECT_COMMENT: 'Required. Tell the employee what to correct - they will see this comment.',
  },

  PM_DASHBOARD: {
    PAGE: 'Summary of the selected month for the active projects you manage (administrators: all active projects).',
    APPROVAL_QUEUE: 'Number of this month\'s submissions on your projects in each status; Submitted ones wait for your review. '
      + 'People who have not submitted at all are not counted (see Team Hours), and the filters do not change these numbers.',
    TEAM_HOURS: 'Hours each person logged on your projects this month, against their required hours for the whole month. '
      + 'Hours on other projects are not counted, so people shared with other projects stay below 100%. '
      + 'Status shows their submissions for your projects.',
    ALERTS: `People under ${CONFIG.UNDERFILL_ALERT_PERCENT}% of their required monthly hours when `
      + `${CONFIG.UNDERFILL_ALERT_DAYS_LEFT} or fewer days of the month are left, and people averaging more than `
      + `${CONFIG.OVERTIME_ALERT_WEEKLY_HOURS}h a week (less for part-time). Only hours on your projects count.`,
    RESOURCE_FILTER: 'Show only these people',
    STATUS_FILTER: 'Show only people with a submission in one of these statuses',
    PROJECT_FILTER: 'Show only people who logged time on these projects (their hours still include all your projects)',
    RESET_FILTERS: 'Clear all filters and go back to the current month',
  },

  ANALYTICS: {
    PAGE: 'A pie chart of your hours for the chosen months. Use the drop-down next to the period to split it by project, '
      + 'activity or client.',
    GROUP_BY: 'What each slice of the chart represents',
  },

  REPORTS: {
    SUMMARY_PAGE: 'Totals of logged hours for the chosen period, grouped the way you choose. It covers your own hours; '
      + 'project managers and administrators also see the hours on their projects and can download the result with Export. '
      + 'Narrow the data with the filters and keep a setup you reuse with Saved Filters.',
    PERIOD: 'The year and months to include; the buttons next to it pick common periods.',
    GROUP_BY: 'How the rows are grouped, for example by project or activity (Resource means person)',
    SUB_GROUP_BY: 'Optional: split each group again, for example each project by activity',
    PROJECT: 'Include only these projects',
    RESOURCE: 'Include only these people',
    ACTIVITY: 'Include only these activities',
    CLIENT: 'Include only projects of these clients',
    SUBMIT_STATUS: 'Include only hours whose monthly submission has one of these statuses (Draft = not submitted yet)',
    BILLABLE_ONLY: 'Count only billable hours',
    SHOW_DETAILS: 'Show the individual time entries instead of totals only',
    GROUP_BY_WEEK: 'Split each row by week',
    SHOW_DAYS: 'Show the hours for each day of the week',
    SHOW_DAYS_BLOCKED: 'Turn on "Group by week" or "Show details" first to see hours per day',
    RESET_FILTERS: 'Clear all filters and settings and go back to the current month',
    SAVED_FILTERS: 'Save the current filters under a name, or load a set you saved earlier (kept in this browser only)',
    EXPORT: 'Download the report as an Excel, CSV or PDF file',
    TIME_AUDIT_PAGE: 'Checks the selected month for hours worth a second look: days with more hours than a full working day '
      + `(${HOURS_PER_DAY}h for full-time, less for part-time) and weekend work. Project managers see hours on their projects, `
      + 'administrators see everyone. "Users Without Time" lists people who logged no hours at all this month.',
    TIME_AUDIT_RESET: 'Clear the person, project and rule filters; the month stays as selected',
    AUDIT_RULE: 'Show only entries that break these rules',
    USERS_WITHOUT_TIME: 'Show or hide the people who have logged no hours in the selected month',
  },

  PROJECTS: {
    PAGE: 'Projects you manage (administrators see all). Create a project, then choose on its Activities tab what can be '
      + 'logged and on its Users tab who can log time. A person can log an assigned activity only if it is allowed for them '
      + '(Users tab or Permissions page). Changes are saved automatically.',
    SEARCH: 'Search by project name, client, department, Dynamics reference or manager',
    CLIENT_FILTER: 'Show only projects of these clients',
    MANAGER_FILTER: 'Show only projects managed by these people',
    BILLABLE_FILTER: 'Show only billable or only non-billable projects',
    CREATED_BY_FILTER: 'Show only projects created by these people',
    UPDATED_BY_FILTER: 'Show only projects last changed by these people',
    STATUS_FILTER: 'Show active, inactive or all projects. The list opens on active ones; only they accept time.',
    FLAT_LIST: 'Show all projects in one table',
    GROUP_BY_MANAGER: 'Group projects by their manager',
    NEW_PROJECT: 'Create a project. A project manager who creates it becomes its manager. It is saved as soon as the '
      + 'required (*) fields are filled in; then you can add activities and people. Projects cannot be deleted, only deactivated.',
    EDIT: 'Edit project details',
    ACTIVITIES: 'Choose which activities can be logged on this project',
    USERS: 'Choose who can log time on this project',
    DEACTIVATE: 'Close the project for new time entries. Inactive projects also drop out of the PM Dashboard.',
    ACTIVATE: 'Reopen the project for time entries',
    COLUMN_DYNAMICS: 'Reference of this project in Microsoft Dynamics',
    COLUMN_MANAGER: 'Project manager. Approves the monthly hours submitted for this project.',
    COLUMN_CREATED_BY: 'Who created the project',
    COLUMN_UPDATED_BY: 'Who changed the project last',
    COLUMN_STATUS: 'Active - people can log time. Inactive - closed for new time entries.',
    CLOSE_PANEL: 'Close the panel',
    AUTOSAVE: 'Changes save automatically about 2 seconds after you stop typing, once all required (*) fields are filled in. '
      + 'A new project is created at that moment.',
    FIELD_DYNAMICS: 'Optional. Reference of this project in Microsoft Dynamics.',
    FIELD_BILLABLE: 'Whether hours on this project can be invoiced to the client; Not set counts as non-billable. '
      + 'Hours are billable only when the activity is billable too. A change applies to hours saved after it.',
    FIELD_DEPARTMENT: 'Department that owns the project',
    FIELD_CLIENT: 'Customer the project is done for. On a new project the client name is added to the project name '
      + 'in brackets.',
    FIELD_MANAGER: 'Approves the hours submitted for this project and needs the Project manager role. '
      + 'Changing it hands the project over at once.',
    ACTIVITIES_TAB: 'Activities are the kinds of work that can be logged on this project (for example Development or Meeting). '
      + 'Pick one or more and click Assign; the trash icon removes one right away. A person can log an assigned activity '
      + 'only if it is allowed for them (Users tab or Permissions page).',
    REMOVE_ACTIVITY: 'Remove this activity from the project right away; hours already logged stay',
    USERS_TAB: 'People listed here have access to this project; they can log only the activities allowed for them. '
      + 'Adding and removing people is saved right away. Click a person to see and change which activities they can log.',
    REMOVE_USER: 'Remove this person from the project right away; hours they already logged stay',
    ACTIVITY_ACCESS: 'Allow - this person can log this activity on this project.\n'
      + 'Deny - they cannot log it on this project.\n'
      + 'Default - it depends on the activities allowed for them in general (set by an administrator); '
      + 'choose Allow if they must log this activity here.\n'
      + 'Each click is saved right away.',
  },

  ACTIVITIES: {
    PAGE: 'Types of work people log time to. People can use an activity only on projects that list it (Projects page) '
      + 'and only if it is allowed for them (Permissions). Billable - can be invoiced when the project is billable too; '
      + 'Non-billable - internal work; Idle - time without project work.',
    SEARCH: 'Search by activity name or department',
    BILLABLE_FILTER: 'Show only activities of these billing types',
    NEW_ACTIVITY: 'Create a new activity',
    EDIT: 'Edit the name, billing type and department. A new billing type or department applies to hours saved after the change.',
    PERMISSIONS: 'Choose who is allowed to use this activity',
    PERMISSIONS_DIALOG: 'Check the people who may use this activity. They will see it on every project they have access to '
      + 'where this activity is assigned. Changes apply when you click Save.',
    COLUMN_BILLABLE: 'Billable - can be invoiced. Non-billable - internal work. Idle - time without project work.',
    COLUMN_DEPARTMENT: 'Department that hours of this activity count toward in reports',
    FIELD_BILLABLE: 'Billable - can be invoiced to the client (when the project is billable too). '
      + 'Non-billable - internal work. Idle - time without project work. A change applies to hours saved after it.',
  },

  RESOURCES: {
    PAGE: 'Everyone who can sign in and log time; people without an active record here cannot use the site. '
      + 'Click a row to edit a person. Their weekly hours and the working days of their business unit '
      + '(Working Hours page) set how many hours they must log each month.',
    ADD: 'Add a person who needs to log time',
    SEARCH: 'Search by account, name, email, department or business unit',
    DEPARTMENT_FILTER: 'Show only people of these departments',
    BUSINESS_UNIT_FILTER: 'Show only people of these business units (offices)',
    STATUS_FILTER: 'Active - can sign in and log time. Inactive - cannot use the site, for example former employees.',
    COLUMN_ACCOUNT: 'Company account, BRIMIT\\username; all the person\'s hours and access are stored under it',
    COLUMN_BUSINESS_UNIT: 'Office location. Decides which working-days calendar applies to the person.',
    COLUMN_CAPACITY: 'Working hours per week (40 = full-time)',
    COLUMN_STATUS: 'Active - can sign in and log time. Inactive - cannot use the site; past hours are kept.',
    FIELD_DOMAIN_NAME_NEW: 'Company account, for example BRIMIT\\username. It cannot be changed later.',
    FIELD_DOMAIN_NAME_EDIT: 'Cannot be changed: all the person\'s hours and access are stored under this account.',
    FIELD_DISPLAY_NAME_SEARCH: 'Type and pick the person from the company directory; this also fills in the email and directory ID.',
    FIELD_EMAIL: 'Used for reminders and to recognize the person at sign-in. Type to search the company directory.',
    FIELD_EMAIL_PLAIN: 'Used for reminders and to recognize the person at sign-in. Filled in when you pick the person above.',
    FIELD_AD_ID: 'Used to recognize the person at sign-in. Filled in when you pick the person from the directory.',
    FIELD_DEPARTMENT: 'Department the person belongs to',
    FIELD_BUSINESS_UNIT: 'Office location. Decides which working-days calendar (Working Hours page) applies.',
    FIELD_MANAGER: 'Line manager, kept with the person\'s monthly submissions. Hours are approved by each project\'s manager.',
    FIELD_CAPACITY: '40 = full-time, 20 = half-time (empty counts as 40). Scales the hours the person must log each month.',
    FIELD_BILLABLE: 'For information only: it does not change what the person can log or whether their hours are billable.',
    FIELD_ACTIVE: 'Turn off when the person leaves: they can no longer use the site and drop out of people lists; past hours stay.',
  },

  PERMISSIONS: {
    PAGE: 'Decide what a person can log time to. A project and activity appear in their timesheet only when:\n'
      + '- the project is active and allowed for them (Projects tab),\n'
      + '- the activity is assigned to that project (Projects page),\n'
      + '- the activity is allowed for them (Activities tab) or allowed for that project (Activity Overrides tab),\n'
      + '- and no override denies it.\n'
      + 'Changes are saved right away.',
    USER_SEARCH: 'Type a name and pick the person whose access you want to change',
    OBJECT_LIST: 'Allow - the person can log time to it. Deny and Not set both mean no access; on the Projects tab, '
      + 'Deny also includes projects the person hid on their My Projects page. Each change is saved right away. '
      + 'Click a group header to open it.',
    OBJECT_FILTER: 'Filter the list by name',
    OVERRIDES: 'An override changes one activity on one project only. Allow lets the person log it there even if the '
      + 'activity is not allowed for them in general; Deny blocks it there even if it is. The project must still be allowed '
      + 'and the activity assigned to it. The same setting is on the Projects page, Users tab.',
    REMOVE_OVERRIDE: 'Remove this override right away; the person\'s Activities tab setting applies on this project again',
    PAIR_NOT_ASSIGNED: 'This activity is not assigned to this project. Assign it on the Projects page first.',
    PICK_PAIR: 'Pick a project and an activity first',
    COLLAPSE_ALL: 'Collapse all groups',
    EXPAND_ALL: 'Expand all groups',
  },

  WORKING_HOURS: {
    PAGE: 'Working days per month for each office (the business unit on the Resources page). A person\'s required monthly '
      + `hours = working days x ${HOURS_PER_DAY}h, scaled by their weekly hours; they must reach them before submitting `
      + 'the month. Offices without their own calendar use Default, so create the Default calendar for every new year.',
    YEAR: 'Choose the year',
    PREV_YEAR: 'Previous year',
    NEXT_YEAR: 'Next year',
    LOCATION: 'Offices are the business units of active people; the number is how many active people work there. '
      + 'Custom - own calendar for this year; Default - uses the Default calendar.',
    COLUMN_HOURS: `Hours in the month = working days x ${HOURS_PER_DAY}. Calculated automatically.`,
    COLUMN_DAYS: 'Type the working days and click Save; the change applies to everyone who uses this calendar.',
    COLUMN_DIFF: 'Difference in hours from the Default calendar',
    DELETE: (location: string, year: number, isDefault: boolean): string => (isDefault
      ? `Delete the Default calendar for ${year}: every office without its own calendar is left with no working days that year. `
      + 'This cannot be undone.'
      : `Delete the ${location} calendar for ${year}; people there fall back to the Default calendar.`),
    RESET: (location: string, year: number): string =>
      `Discard the custom hours of ${location} and use the Default values for ${year}.`,
    SAVE: 'Save all changed months',
    INITIALIZE: (location: string, year: number): string =>
      `Create the ${year} calendar for ${location} by copying the Default calendar of the latest earlier year; `
      + 'then adjust the working days and click Save.',
    EMPTY_DEFAULT: (year: number): string =>
      `There is no Default calendar for ${year} yet, so offices that use Default have no working days that year. `
      + 'Click Initialize Year to create it.',
    EMPTY_CUSTOM: (location: string, year: number): string =>
      `${location} has no own calendar for ${year} and uses the Default one. `
      + 'Click Initialize Year only if this office needs different working days.',
  },

  DELEGATIONS: {
    PAGE: 'A delegation lets one person (Account) open and fill in another person\'s timesheet (Resource), for example a '
      + 'stand-in during a vacation. The Account picks that person at the top of their Timesheet page but cannot submit '
      + 'the month for them. Administrators also need a delegation to edit someone\'s timesheet. '
      + 'A delegation stays until you remove it.',
    ACCOUNT_FILTER: 'Show delegations given to these people (who fill in)',
    RESOURCE_FILTER: 'Show delegations for these people (whose timesheet is filled in)',
    ADD: 'Allow a person to fill in someone else\'s timesheet',
    REMOVE: 'Remove this delegation right away; hours already entered stay, but the Account can no longer open this timesheet',
    COLLAPSE_ALL: 'Collapse all groups',
    EXPAND_ALL: 'Expand all groups',
    TOGGLE_GROUP: 'Show or hide the timesheets this person can fill in',
    GROUP_COUNT: 'Number of people whose timesheet this person can fill in',
    FIELD_ACCOUNT: 'The person who will open and fill in the timesheet',
    FIELD_RESOURCE: 'The person whose timesheet will be filled in',
  },

  PROFILE: {
    ROLE_ADMIN: 'Administrator - manages projects, people, permissions and settings. Roles come from company directory groups.',
    ROLE_PM: 'Project manager - manages own projects and approves their hours. Roles come from company directory groups.',
    ROLE_USER: 'User - logs own time. Roles come from company directory groups.',
    BUSINESS_UNIT: 'Your office location; its public holidays and working days set how many hours you must log each month.',
    CAPACITY: 'Your working hours per week (40 = full-time). They set your daily and monthly required hours; '
      + 'if the value is wrong, contact an administrator.',
    SHORTCUTS: 'List of keyboard shortcuts. You can also press ? on any page.',
  },
} as const;
